import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Chip } from '@/components/Chip';
import { Icon } from '@/components/Icon';
import { fetchTownCatalog } from '@/lib/api';
import type { SearchScope } from '@/lib/api';
import { COUNTRIES, countryMeta } from '@/lib/countries';
import { canFollowCountry, TIERS, type CountryCode } from '@/lib/tier';
import { useTier } from '@/session/SessionProvider';
import './ScopeChooser.css';

/**
 * The search-in scope chooser.
 *
 * Sits directly under the input and stays visible before and after a query — a user has to be
 * able to see what is being searched at the moment they read the results, not only at the
 * moment they typed.
 *
 * This exists because an earlier version silently applied default filters: wrong body type,
 * wrong state. The fix was to default to zero filters and state the scope out loud. So the
 * summary line below is not decoration; it is the correction.
 *
 * Built on the same country catalogue as Add Town so the vocabulary matches — "provinces" in
 * Canada, "nations" in the UK.
 */
export function ScopeChooser({
  scope,
  onScopeChange,
  country,
  onCountryChange,
  regions,
  onRegionsChange,
  followedTownNames,
  corpusSummary,
}: {
  scope: SearchScope;
  onScopeChange: (scope: SearchScope) => void;
  country: CountryCode;
  onCountryChange: (country: CountryCode) => void;
  /** Selected region keys, e.g. "US/NJ". Multi-select. */
  regions: string[];
  onRegionsChange: (regions: string[]) => void;
  followedTownNames: string[];
  /** "over 245,000 meetings · over 3,800 places · every body type" */
  corpusSummary: string;
}) {
  const tier = useTier();
  const meta = countryMeta(country);

  const catalogQuery = useQuery({
    queryKey: ['catalog', country],
    queryFn: () => fetchTownCatalog(country),
    enabled: scope === 'pick',
  });

  /** Place counts per region, so a user can see how much they are about to search. */
  const regionRows = useMemo(() => {
    const counts = new Map<string, { name: string; towns: number }>();
    for (const unit of catalogQuery.data ?? []) {
      const existing = counts.get(unit.regionCode);
      counts.set(unit.regionCode, {
        name: unit.regionName,
        towns: (existing?.towns ?? 0) + unit.towns.length,
      });
    }
    return [...counts.entries()].map(([code, v]) => ({ code, ...v }));
  }, [catalogQuery.data]);

  const chosen = regions.filter((r) => r.startsWith(`${country}/`));

  const summary =
    scope === 'all'
      ? corpusSummary
      : scope === 'mine'
        ? `${followedTownNames.join(', ')} · both bodies`
        : (() => {
            const townsIn = regionRows
              .filter((r) => chosen.length === 0 || chosen.includes(`${country}/${r.code}`))
              .reduce((n, r) => n + r.towns, 0);
            const where =
              chosen.length > 0
                ? `${chosen.length} ${meta.region.toLowerCase()}${chosen.length === 1 ? '' : 's'}`
                : `all of ${meta.name}`;
            return `${where} · ${townsIn} towns`;
          })();

  const MODES: { key: SearchScope; label: string; hint: string }[] = [
    { key: 'all', label: 'Everywhere', hint: 'every place' },
    {
      key: 'mine',
      label: 'What I follow',
      hint: `${followedTownNames.length} followed`,
    },
    { key: 'pick', label: 'Pick places', hint: `${meta.region.toLowerCase()}s` },
  ];

  return (
    <div className="scope">
      <div className="scope__modes" role="tablist" aria-label="Search in">
        {MODES.map((mode) => (
          <button
            key={mode.key}
            type="button"
            role="tab"
            aria-selected={scope === mode.key}
            className="scope__mode"
            data-active={scope === mode.key}
            onClick={() => onScopeChange(mode.key)}
          >
            <span className="scope__modeLabel">{mode.label}</span>
            <span className="scope__modeHint">{mode.hint}</span>
          </button>
        ))}
      </div>

      {/* The scope badge. Green and always present, so nobody has to guess what was searched. */}
      <p className="scope__badge" data-full={scope === 'all'}>
        <Icon name="check" />
        <span className="scope__badgeLabel">
          {scope === 'all' ? 'Full corpus' : scope === 'mine' ? 'What I follow' : 'Selected places'}
        </span>
        <span className="scope__summary">{summary}</span>
      </p>

      {scope === 'pick' && (
        <div className="scope__picker">
          <div className="scope__row u-scroller" aria-label="Country">
            {COUNTRIES.map((c) => {
              const unlocked = canFollowCountry(tier, c.code);
              if (!unlocked) {
                return (
                  <Link key={c.code} to="/pricing" className="scope__country" data-locked="true">
                    <Icon name="lock" /> {c.name}
                  </Link>
                );
              }
              return (
                <button
                  key={c.code}
                  type="button"
                  className="scope__country"
                  data-selected={c.code === country}
                  onClick={() => onCountryChange(c.code)}
                >
                  {c.name}
                </button>
              );
            })}
          </div>

          <div className="scope__row u-scroller" aria-label={`${meta.region}s`}>
            {regionRows.map((r) => {
              const key = `${country}/${r.code}`;
              const on = regions.includes(key);
              return (
                <Chip
                  key={key}
                  label={
                    <>
                      {r.name} <span className="scope__count">{r.towns}</span>
                    </>
                  }
                  selected={on}
                  onClick={() =>
                    onRegionsChange(
                      on ? regions.filter((x) => x !== key) : [...regions, key],
                    )
                  }
                />
              );
            })}
            {chosen.length > 0 && (
              <button type="button" className="scope__clear" onClick={() => onRegionsChange([])}>
                Clear
              </button>
            )}
          </div>

          {TIERS[tier].countries.length < COUNTRIES.length && (
            <p className="scope__note">
              Your plan covers{' '}
              {TIERS[tier].countries.map((c) => countryMeta(c).name).join(' and ')}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
